/* drag.js — the interactive drag glue, ported from lhc-app.html as PURE functions.
 * planScreenToWorld (~36014), _movePickupAt (~37702), _moveDragTo (~37724), _moveCommit
 * (~37783). The DOM/pointer event wiring (mousedown/move/up, SVG getScreenCTM) lives with
 * the UI; here we port the math so it's testable and reusable: screen↔world with zoom/pan,
 * the pickup grab-offset, the per-move wall-snap + contact clamp, and the commit validate/snap.
 */
import { getWallPlacementInfo } from "./placement";
import { snapToNearestWall, snapSides, clampAbsLeftToSpan } from "./snapping";
import { clampToContact, snapFindPlacementAt, snapExactFitEx } from "./contact";
import { validatePlacement } from "./validation";
import { quantMm } from "./precision";

/** Invert a 2D affine matrix {a,b,c,d,e,f} (screen = M·user). */
export function invertAffine(m) {
  const det = m.a * m.d - m.b * m.c;
  if (!det) return null;
  const id = 1 / det;
  return {
    a: m.d * id, b: -m.b * id, c: -m.c * id, d: m.a * id,
    e: (m.c * m.f - m.d * m.e) * id, f: (m.b * m.e - m.a * m.f) * id,
  };
}
export function applyAffine(m, x, y) { return [m.a * x + m.c * y + m.e, m.b * x + m.d * y + m.f]; }

/** SVG user-space point → world metres. transform = { minX, minZ, SCALE } (px per metre). (~36023) */
export function screenUserToWorld(sx, sz, transform) {
  return [transform.minX + sx / transform.SCALE, transform.minZ + sz / transform.SCALE];
}

/** client (screen px) → world metres, honouring zoom/pan via the SVG screenCTM. (~36014)
 *  screenCTM maps user→screen; we invert it to get user, then map user→world. */
export function clientToWorld(clientX, clientY, screenCTM, transform) {
  const inv = invertAffine(screenCTM);
  if (!inv) return null;
  const [ux, uy] = applyAffine(inv, clientX, clientY);
  return screenUserToWorld(ux, uy, transform);
}

/** Grab offset so the cabinet doesn't jump when picked up. (~37710) */
export function pickupOffsetAlong(hit, def, world, walls) {
  const wall = hit.wallId ? walls.find((w) => w.id === hit.wallId) : null;
  const info = wall ? getWallPlacementInfo(wall, walls) : null;
  const clickAlong = info ? (world[0] - wall.start[0]) * info.dirX + (world[1] - wall.start[1]) * info.dirZ : world[0];
  return clickAlong - (hit.absLeft + (def ? def.width / 2 : 0));
}

/** Hit-test wrapper (which placement is under a world point). */
export function pickAt(world, placements, lib, walls) {
  return snapFindPlacementAt(world[0], world[1], placements, lib, walls);
}

/** Per-move target from a world point: snap to nearest wall + contact-clamp, else project
 *  onto the current wall and clamp to its length. Returns { wallId, absLeft }. (~37724) */
export function dragMoveTo(p, def, world, walls, placements, lib, moveOffsetAlong = 0, snapDist = 0.8) {
  // preferWallId = the cabinet's current wall: sticky, deterministic ownership near corners.
  const snap = snapToNearestWall(world[0], world[1], (def && def.width) || 0.6, walls, snapDist, p.wallId || null);
  if (snap) {
    const wallId = snap.wall.id;
    const fromLeft = p.wallId === wallId && typeof p.absLeft === "number" ? p.absLeft : null;
    // FACE PICK (2026-07-14 peninsula fix, v0.33 parity for MOVES): on a
    // double-sided wall the cursor side chooses the face — dragging a cabinet
    // across the peninsula flips it to the other run, exactly like drops do.
    let faceSign = p.faceSign;
    const _w = snap.wall;
    if (_w.style === "partition" || _w.style === "peninsula") {
      const _mx = (_w.start[0] + _w.end[0]) / 2, _mz = (_w.start[1] + _w.end[1]) / 2;
      const _dx = _w.end[0] - _w.start[0], _dz = _w.end[1] - _w.start[1];
      const _l = Math.hypot(_dx, _dz) || 1;
      faceSign = ((world[0] - _mx) * (-_dz / _l) + (world[1] - _mz) * (_dx / _l)) >= 0 ? 1 : -1;
    } else {
      /* STALE FACE HYGIENE (2026-08-29 adversarial review): a face only exists
       * on a partition/peninsula. A cabinet dragged OFF a peninsula onto an
       * ordinary wall used to keep its old ±1 forever — invisible to layout
       * (cabinetWorldPos ignores it there) but poison for anything reading
       * faceSign as a fact, e.g. the backsplash back-to-back rule. null tells
       * the committer to DELETE the field. */
      faceSign = null;
    }
    const probe = { id: p.id, type: p.type, wallId, absLeft: snap.absLeft, config: p.config, faceSign };
    // TIGHT-GAP AUTO-SEAT (2026-07-14, Issue 4): a gap that fits this cabinet
    // almost exactly has one legal position — seat the drag into it instantly.
    // Checked BEFORE the contact clamp: a both-sides-touching seat sits inside
    // the clamp's merged blocked interval and would be pushed away otherwise.
    const fit = snapExactFitEx(probe, def, snap.absLeft, placements, lib, walls);
    if (fit.seated) return { wallId, absLeft: fit.absLeft, faceSign };
    const absLeft = clampToContact(probe, def, snap.absLeft, fromLeft, placements, lib, walls);
    return { wallId, absLeft, faceSign };
  }
  const wall = p.wallId ? walls.find((w) => w.id === p.wallId) : null;
  const info = wall ? getWallPlacementInfo(wall, walls) : null;
  const wLen = info ? info.len : 0;
  const along = info ? (world[0] - wall.start[0]) * info.dirX + (world[1] - wall.start[1]) * info.dirZ : world[0];
  const raw = along - moveOffsetAlong - (def ? def.width / 2 : 0);
  // Signed span clamp — a 0 floor here pinned the fallback path to the centerline node.
  const absLeft = wall
    ? clampAbsLeftToSpan(raw, def ? def.width : 0, wall, walls)
    : Math.max(0, Math.min(raw, wLen - (def ? def.width : 0)));
  return { wallId: p.wallId, absLeft };
}

/** Is a candidate position valid (for invalid-ghost rendering during drag)? */
export function isDropValid(candidate, placements, lib, walls, wizard, openings) {
  return validatePlacement(candidate, placements, lib, walls, wizard, openings).ok;
}

/** Commit a move: validate → revert to orig on failure → apply the neighbour magnet. (~37783)
 *  Returns { ok, absLeft, wallId, reason }. */
export function commitMove(p, walls, placements, lib, wizard, openings, orig, moveDir) {
  const v = validatePlacement(p, placements, lib, walls, wizard, openings);
  if (!v.ok) {
    if (orig) { p.wallId = orig.wallId; p.absLeft = orig.absLeft; }
    return { ok: false, absLeft: p.absLeft, wallId: p.wallId, reason: v.reason };
  }
  p.absLeft = snapSides(p, placements, lib, walls, moveDir);
  // CAD PRECISION (Issue 5): interactive commits land on the 1mm grid — the
  // stored coordinate always reads back as the same whole millimetre.
  p.absLeft = quantMm(p.absLeft);
  return { ok: true, absLeft: p.absLeft, wallId: p.wallId };
}
